/**
 * The kaizen part of the console API (design 13, 19, 8.2): eval cases people write and import,
 * comparing models by evals and proposing a route (C 8.2-02), and reporting a mistake on an AI message
 * (I 19-10). `ConsoleApi` extends it and `ConsoleClient` mixes `kaizenMethods` in, like the trust part.
 */
import type { ChangeSetView } from '@takeit4me/shared'
import type { Http } from './http'
import { qs } from './http'

export type EvalCaseOrigin = 'history' | 'shadow' | 'manual' | 'import' | 'mistake' | 'other'
/** `system`: an automatic message the business sent and the customer saw (`source`: a journey chase text, an agent, other). */
export interface EvalCaseTurn { role: 'customer' | 'assistant' | 'staff' | 'system'; text: string; source?: 'journey' | 'agent' | 'automatic' }
export interface EvalCaseRecord { object: string; label?: string; title?: string | null; data: Record<string, unknown>; money_fields?: string[] }
export interface EvalCasePayment { kind: string; amount_minor: number; currency: string; status: string; date?: string | null }
export interface EvalCaseInput {
  text: string; subject?: string | null; channel: 'email' | 'sms' | 'chat' | 'voice'; request_type?: string | null
  history?: EvalCaseTurn[]; records?: EvalCaseRecord[]; payments?: EvalCasePayment[]; customer_name?: string | null
}
export interface EvalCaseExpect {
  must_include?: string[]; must_include_one_of?: string[]; must_not_include?: string[]
  amounts_minor?: number[]; forbidden_amounts_minor?: number[]; allowed_amounts_minor?: number[]; amounts_from_context?: boolean
  handover?: 'never' | 'expected' | null; resolved?: boolean | null; body_equals?: string | null; advisory?: 'resolved'[]
  request_type?: string | null; rubric?: string; critical?: boolean
}
export interface EvalCaseView {
  id: string; suite: string; name: string; source: string | null; origin: EvalCaseOrigin; active: boolean; created_at: string
  input: EvalCaseInput & Record<string, unknown>; expect: EvalCaseExpect & Record<string, unknown>
  /** The label was guessed and waits for a person to confirm it (until then a miss only advises). */
  needs_review?: boolean
  /** The guessed or confirmed label: resolved (conversations) or fires (classification). */
  label?: boolean | null
  curated?: { by: string; by_name: string; at: string; label: boolean } | null
}
export interface EvalCaseBody { name: string; input: EvalCaseInput; expect: EvalCaseExpect }
export interface EvalImportBody { content: string; format?: 'json' | 'csv' | 'auto'; filename?: string | null }
export interface EvalImportResult { imported: number; updated: number; skipped: { name: string; reason: string }[]; format: string }

export interface ModelRouteRun {
  route: string[]; current: boolean; passed: number; failed: number; pass_rate: number | null
  purpose_cost_micros: number; cost_micros: number; cost_per_case_micros: number | null
  calls: number; failures: number; fallbacks: number; latency_p50_ms: number | null; latency_p95_ms: number | null
  worse: string[]; better: string[]; error: string | null
  results: { case_id: string | null; name: string; ok: boolean; detail: string | null }[]
}
export interface ModelComparisonView {
  id: string; purpose: string; suite: string; cases: number; created_at: string; status: string
  runs: ModelRouteRun[]
  recommendation: { keep: boolean; route: string[] | null; reason: string } | null
  change_set_id: string | null
  deployment_value: string | null
}
export interface ModelCompareView { purposes: { purpose: string; suite: string; route: string[] }[]; models: string[]; comparisons: ModelComparisonView[] }
export interface ModelCompareBody { purpose: string; candidates: string[]; limit?: number }

export interface MistakeReportBody { reason: string; correction?: string | null }
export interface MistakeReportResult {
  ok: true; audit_id: string; eval_case_id: string | null; exception_id: string; labels: number
  demoted: { autonomy_id: string; from: number; to: number } | null; error_budget_alarm: boolean
}

export interface KaizenApi {
  /** Eval cases of a suite (or all), newest first, with where each came from. */
  evalCases(q?: { suite?: string; origin?: EvalCaseOrigin }): Promise<EvalCaseView[]>
  /** A conversation case written by hand (supervisors and admins). */
  createEvalCase(body: EvalCaseBody): Promise<EvalCaseView>
  /** Retires a case (it no longer runs) or brings it back. */
  setEvalCaseActive(id: string, active: boolean): Promise<EvalCaseView>
  /** Imports a reference set: JSON (ours or the case-context format) or CSV. Importing again updates by name. */
  importEvalCases(body: EvalImportBody): Promise<EvalImportResult>
  /** The purposes that can be compared, the models, and recent comparisons. */
  modelComparisons(): Promise<ModelCompareView>
  /** Runs a purpose's eval suite on today's route and each candidate: quality, cost and latency side by side. */
  compareModels(body: ModelCompareBody): Promise<ModelComparisonView>
  /** Proposes a compared route as a tested change set for this company (an admin approves it before release). */
  proposeModelRoute(comparisonId: string, route: string[]): Promise<ChangeSetView>
  /** Reports an AI message that reached the customer as a critical error: graded at once, autonomy drops. */
  reportMistake(messageId: string, body: MistakeReportBody): Promise<MistakeReportResult>
}

const enc = encodeURIComponent

export function kaizenMethods(http: Http): KaizenApi {
  return {
    evalCases: (q = {}) => http.get<EvalCaseView[]>(`/evals/cases${qs(q as Record<string, string | undefined>)}`),
    createEvalCase: (body) => http.post<EvalCaseView>('/evals/cases', body),
    setEvalCaseActive: (id, active) => http.patch<EvalCaseView>(`/evals/cases/${enc(id)}`, { active }),
    importEvalCases: (body) => http.post<EvalImportResult>('/evals/import', body),
    modelComparisons: () => http.get<ModelCompareView>('/evals/compare'),
    compareModels: (body) => http.post<ModelComparisonView>('/evals/compare', body),
    proposeModelRoute: (id, route) => http.post<ChangeSetView>(`/evals/compare/${enc(id)}/propose`, { route }),
    reportMistake: (id, body) => http.post<MistakeReportResult>(`/messages/${enc(id)}/report-mistake`, body),
  }
}
